import { FC } from "react";

import { Typography } from "antd";

import { Coverage, Engine, InsurancePolicy, Vessel } from "../../../../../../../shared/interfaces";
import { Column, _renderCard, _renderColumns } from "../../../../utils/renderSections";
import { formattedPrice } from "../../../../../../../shared/utils";

import { CollapsableSections } from "..";
import { ColSize, RowSize } from "../../../../../../../shared/components";


interface VesselsPossessionsCollapsableProps {
    possessionInfo: Vessel;
    insurancePolicyDetail: InsurancePolicy;
}

export const VesselsPossessionsCollapsable: FC<VesselsPossessionsCollapsableProps> = ({ possessionInfo, insurancePolicyDetail }) => {

    const generateGeneralData = (): Array<Column> => ([
        {
            label: 'Tipo',
            value: possessionInfo.tipo,
        },
        {
            label: 'Nombre',
            value: possessionInfo.nombre,
        },
        {
            label: 'Astillero',
            value: possessionInfo.astillero,
        },
        {
            label: 'Matrícula',
            value: possessionInfo.matricula,
        },
        {
            label: 'Modelo',
            value: possessionInfo.modelo,
        },
        {
            label: 'Año',
            value: possessionInfo.anio,
        },
        {
            label: 'Material',
            value: possessionInfo.material,
        },
        {
            label: 'Avaluado',
            value: `${ insurancePolicyDetail.moneda }${ formattedPrice(possessionInfo.avaluado) }`,
        },
        {
            label: 'Suma asegurada casco',
            value: `${ insurancePolicyDetail.moneda }${ formattedPrice(possessionInfo.sumaCasco) }`,
        },
        {
            label: 'Suma asegurada motor',
            value: `${ insurancePolicyDetail.moneda }${ formattedPrice(possessionInfo.sumaMotor) }`,
        },
        {
            label: 'Suma asegurada total',
            value: `${ insurancePolicyDetail.moneda }${ formattedPrice(possessionInfo.sumaTotal) }`,
        },
        {
            label: 'Eslora',
            value: `${ possessionInfo.eslora } MTS`,
        },
        {
            label: 'Manga',
            value: `${ possessionInfo.manga } MTS`,
        },
        {
            label: 'Puntal',
            value: `${ possessionInfo.puntal } MTS`,
        },
    ]);

    const generateCoverageCard = ({ nombre, sumaAsegurada, tasa, prima }: Coverage): Array<Column> => ([
        {
            label: 'Cobertura',
            value: nombre,
            columnSize: { xs: { span: 12 }, lg: { span: 6 } }
        },
        {
            label: 'Suma asegurada',
            value: `${ insurancePolicyDetail.moneda }${ formattedPrice( sumaAsegurada ) }`,
            columnSize: { xs: { span: 12 }, lg: { span: 6 } }
        },
        {
            label: 'Tasa',
            value: `${ insurancePolicyDetail.moneda }${ formattedPrice( tasa ) }`,
            columnSize: { xs: { span: 12 }, lg: { span: 6 } }
        },
        {
            label: 'Prima',
            value: `${ insurancePolicyDetail.moneda }${ formattedPrice( prima ) }`,
            columnSize: { xs: { span: 12 }, lg: { span: 6 } }
        },
    ]) 

    const generateProduct = (): Array<Column> =>  [
        {
            label: 'Producto',
            value: possessionInfo.producto,
            valueInBold: true,
            columnSize: { xs: { span: 24 } }
        },

        {
            label: '',
            value: (
                <ColSize size={100}>
                    {
                        possessionInfo.coberturas.length
                            ?   possessionInfo.coberturas.map(( coverage, key ) => _renderCard( key, generateCoverageCard( coverage )) )
                            :   <RowSize rowProps={{gutter:[0 , 0]}}> <Typography.Text> No posee coberturas </Typography.Text> </RowSize>
                    }
                </ColSize>
            ),
            columnSize: { xs: { span: 24 } }
        }
    ]

    const generateEngine = ({ fabrica, modelo, numero, anio, tipo }: Engine ): Array<Column> => ([
        {
            label: 'Fabrica',
            value: fabrica,
            columnSize: { lg: { span: 5 }, xs: { span: 12 }, md: { span: 8 } }
        },
        {
            label: 'Modelo',
            value: modelo,
            columnSize: { lg: { span: 5 }, xs: { span: 12 }, md: { span: 8 } }
        },
        {
            label: 'Número',
            value: numero,
            columnSize: { lg: { span: 5 }, xs: { span: 12 }, md: { span: 8 } }
        },
        {
            label: 'Año',
            value: anio,
            columnSize: { lg: { span: 4 }, xs: { span: 12 }, md: { span: 8 } }
        },
        {
            label: 'Tipo',
            value: tipo,
            columnSize: { lg: { span: 5 }, xs: { span: 12 }, md: { span: 8 } }
        },
    ]);

    const particularClauses: Array<Column> = [
        {
            label: '',
            value: possessionInfo.clausulas.length ? possessionInfo.clausulas.map( el => el.clausula ).join(' - ') : 'No existen clásulas particulares',
            columnSize: { xs: { span: 24 } }
        }
    ];
    
    return (
        <>
            <CollapsableSections 
                title="Datos generales"
                children={  
                    <RowSize rowProps={{gutter:[0 , 10]}} >
                        { _renderColumns( generateGeneralData() ) }
                    </RowSize> 
                }
            />

            <CollapsableSections 
                title="Motores"
                children={
                    <>
                        {
                            possessionInfo.motores.length
                                ? possessionInfo.motores.map(( engine, key ) => _renderCard( key, generateEngine( engine ) ))
                                : <RowSize rowProps={{gutter:[0 , 0]}}> <Typography.Text> No posee motores </Typography.Text> </RowSize>
                        }
                    </>
                }
            />

            <CollapsableSections 
                title="Producto y coberturas"
                children={
                    <RowSize rowProps={{gutter:[0 , 0]}}>
                        { _renderColumns(generateProduct()) }
                    </RowSize>
                }
            />

            <CollapsableSections 
                title="Clásulas Particulares"
                children={
                    <RowSize rowProps={{gutter:[0 , 10]}} >
                        { _renderColumns( particularClauses ) }
                    </RowSize>
                }
            />
        </>
    )
}